import { useState } from 'react';
// @mui
import {
  Box,
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import useTable, { emptyRows } from 'src/hooks/useTable';
import Scrollbar from 'src/components/Scrollbar';
import { TableNoData, TableEmptyRows, TableHeadCustom } from 'src/components/table';
import { Item } from 'src/@types/Course';
import SectionTableToolBar from './SectionItemTableToolbar';
import SectionTableItemRow from './SectionItemTableRow';
import ConfirmationDialog from './ConformiationDialog';
import { CLEAR_SEARCH_TO_REORDER_TEXT, edgeFlags, realIndexOf, rowKey } from '../lessonOrder';

// Plain labels: no column sort. The table always shows the real lesson order,
// which is the order students see (plan R1 T5).
const TABLE_HEAD = [
  { id: 'title', label: 'Title', align: 'left' },
  { id: 'type', label: 'Type', align: 'left' },
  { id: 'points', label: 'points', align: 'left' },
  { id: 'duration', label: 'Duration', align: 'left' },
  { id: '' },
];

// ----------------------------------------------------------------------

export default function SectionsItemTable({
  items,
  onEdit,
  onDeleteItem,
  onMove,
  saving = false,
  readOnly = false,
  locked = false,
}: {
  items: Item[];
  // Every action is addressed by the lesson's uuid, never by a table row index.
  onEdit(uuid: string): void;
  onDeleteItem(uuid: string): void;
  onMove(uuid: string, delta: number): void;
  saving?: boolean;
  readOnly?: boolean;
  // Lesson problems wait for "Fix lesson problems".
  locked?: boolean;
}) {
  const { dense, page, rowsPerPage, setPage, onChangePage, onChangeRowsPerPage } = useTable();

  const [filterName, setFilterName] = useState('');
  const [confirmOpen, setConfirmOpen] = useState<{ open: boolean; uuid: string | undefined }>({
    open: false,
    uuid: undefined,
  });

  const handleFilterName = (value: string) => {
    setFilterName(value);
    setPage(0);
  };

  const lessons = (items ?? []).filter(Boolean);
  const dataFiltered = applyFilter({ tableData: lessons, filterName });
  const filtered = filterName.trim() !== '';
  const showArrows = !filtered && !readOnly;
  const disabled = saving || locked;

  // A delete can leave the current page past the end: show the last page instead.
  const lastPage = Math.max(0, Math.ceil(dataFiltered.length / rowsPerPage) - 1);
  const shownPage = Math.min(page, lastPage);

  const isNotFound = !dataFiltered.length;
  return (
    <>
      <Container
        sx={{
          paddingLeft: '0px !important',
          paddingRight: '0px !important',
          maxWidth: 'unset !important',
        }}
      >
        <Card>
          <SectionTableToolBar filterName={filterName} onFilterName={handleFilterName} />
          {filtered && !readOnly && lessons.length > 1 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, pb: 1, display: 'block' }}
              data-testid="lessons-clear-search-hint"
            >
              {CLEAR_SEARCH_TO_REORDER_TEXT}
            </Typography>
          )}

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom headLabel={TABLE_HEAD} rowCount={dataFiltered.length} />

                <TableBody>
                  {dataFiltered
                    .slice(shownPage * rowsPerPage, shownPage * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const realIndex = lessons.indexOf(row);
                      const uuid = row.uuid as string;
                      const { isFirst, isLast } = edgeFlags(lessons, uuid);
                      return (
                        <SectionTableItemRow
                          key={rowKey(row, realIndex)}
                          row={row}
                          selected={false}
                          isFirst={isFirst}
                          isLast={isLast}
                          showArrows={showArrows}
                          disabled={disabled}
                          readOnly={readOnly}
                          onMoveUp={() => onMove(uuid, -1)}
                          onMoveDown={() => onMove(uuid, 1)}
                          onEditRow={() => onEdit(uuid)}
                          onDeleteRow={() => setConfirmOpen({ open: true, uuid })}
                        />
                      );
                    })}

                  <TableEmptyRows
                    height={2.2}
                    emptyRows={emptyRows(shownPage, rowsPerPage, dataFiltered.length)}
                  />

                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25]}
              component="div"
              count={dataFiltered.length}
              rowsPerPage={rowsPerPage}
              page={shownPage}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>
      </Container>
      <ConfirmationDialog
        isOpen={confirmOpen.open}
        onClose={() => setConfirmOpen({ open: false, uuid: undefined })}
        confirmAction={() => {
          // Still in this section? (the list may have changed while the dialog was open)
          if (confirmOpen.uuid && realIndexOf(lessons, confirmOpen.uuid) >= 0) {
            onDeleteItem(confirmOpen.uuid);
          }
        }}
        loading={saving}
      />
    </>
  );
}

// ----------------------------------------------------------------------

// Title search only (no sort): keeps the real order of the lessons it shows.
function applyFilter({ tableData, filterName }: { tableData: Item[]; filterName: string }) {
  const needle = filterName.trim().toLowerCase();
  if (!needle) return tableData;
  return tableData.filter((item) =>
    [item?.title, item?.title_ar].some(
      (t) => typeof t === 'string' && t.toLowerCase().indexOf(needle) !== -1
    )
  );
}
